import React, { useState, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import PaginationControls from '../../components/PaginationControls';
import { getAllNews, deleteNews, updateNews, createNews } from '../../API/Crud/News.js';
import ConfirmationModal from '../../components/ConfirmationModal';
import DashboardTemplate from '../template/DashboardTemplate';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react'
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { get, post, upload } from '../../services/ApiRouting';
import { useDropzone } from "react-dropzone";
import { BiEdit, BiTrash } from 'react-icons/bi';
import { getAllCategories } from '../../API/Crud/Category';

function News() {
  const [News, setNews] = useState([]);
  const [Categories, setCategories] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [selectedNew, setSelectedNew] = useState(null);
  const { register, handleSubmit, control, reset, setValue, formState: { errors }, clearErrors } = useForm({
    defaultValues: {
      title: "",
      description: "",
      file: [],
    }
  });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [newToDelete, setNewToDelete] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    title: '',
    description: '',
    file: [],
  });
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    const storedToken = sessionStorage.getItem('token');
    if (storedToken) {
      setToken(storedToken);
    } else {
      setError('No token found. Please log in.');
    }
  }, []);

  useEffect(() => {
    if (!token) return;

    async function fetchNews() {
      setLoading(true);
      try {
        const data = await getAllNews();
        const cats = await getAllCategories();
        setCategories(cats.data);
        setNews(data.data);
        setError(null);
      } catch (error) {
        setError(error.message || 'An unknown error occurred');
        toast.error('Failed to load News');
      } finally {
        setLoading(false);
      }
    }
    fetchNews();
  }, [token]);

  const handleDeleteNew = async (newId) => {
    try {
      await deleteNews(newId);
      setNews((prevNews) => prevNews.filter(item => item.id !== newId));
      toast.success('New deleted successfully');
    } catch (error) {
      setError('Error deleting new');
      toast.error('Failed to delete new');
    }
  };

  useEffect(() => {
    if (selectedNew) {
      const existingFiles = selectedNew.image ? [selectedNew.image] : [];
      reset({
        title: selectedNew.title || "",
        description: selectedNew.description || "",
        file: existingFiles,
      });
      setUploadedFiles(existingFiles);
    }
  }, [selectedNew, reset]);

  const handleEditNew = (news) => {
    setSelectedNew(news);
    setFormData({
      id: news.id,
      title: news.title,
      description: news.description,
      file: news.file,
    });
    setShowEditModal(true);
  };

  // Upload function
  const uploadFiles = async (files, field) => {
    setIsUploading(true);
    const uploadPromises = files.map(async (file) => {
      try {
        const formData = new FormData();
        formData.append('file', file, file.name);

        const response = await upload(`${API_URL}/crud/news/fileUpload`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        const serverFilePath = response.fileId || response.data.file_path;
        return serverFilePath;
      } catch (err) {
        console.error("Upload error:", err);
        return null;
      }
    });

    const serverIds = await Promise.all(uploadPromises);
    const validServerIds = serverIds.filter(Boolean);

    const newFiles = [...validServerIds];
    setUploadedFiles(newFiles);
    field.onChange(newFiles);
    clearErrors('file');

    setIsUploading(false);
  };

  // Remove file function
  const removeFile = (index, field) => {
    const newFiles = uploadedFiles.filter((_, i) => i !== index);
    setUploadedFiles(newFiles);
    field.onChange(newFiles);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const baseUrl = `${API_PUBLIC_URL}`;

  const handleConfirmDelete = () => {
    if (newToDelete) {
      handleDeleteNew(newToDelete.id);
      setShowConfirmModal(false);
      setNewToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setShowConfirmModal(false);
    setNewToDelete(null);
  };

  const onSubmit = async (data) => {
    if (uploadedFiles.length === 0) {
      toast.error("Image is required");
      return;
    }
    const newsData = {
      ...data,
      file: uploadedFiles,
    };
    try {
      const response = await createNews(newsData);
      if (response.ok) {
        setShowModal(false);
        toast.success('News added successfully');
        setNews((prevNews) => [...prevNews, response.data]);
        reset({
          title: "",
          description: "",
          file: [],
        });
        setUploadedFiles([]);
      }
    } catch (error) {
      console.error("Error creating new:", error);
    }
  };

  const onUpdateSubmit = async (data) => {
    if (uploadedFiles.length === 0) {
      toast.error("Image is required");
      return;
    }
    const newsData = {
      ...data,
      file: uploadedFiles,
    };
    try {
      const response = await updateNews(selectedNew.id, newsData);
      if (response.ok) {
        setShowEditModal(false);
        toast.success('News updated successfully');
        setNews((prevNews) =>
          prevNews.map((item) =>
            item.id === selectedNew.id ? { ...item, ...newsData } : item
          )
        );
      }
    } catch (error) {
      console.error("Error updating news:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <DashboardTemplate>
      <div className="flex flex-col">
        <ToastContainer />
        <div className="transition-all duration-500 flex-1">
          <div className="content p-2 md:p-5">
            {loading ? (
              <svg xmlns="http://www.w3.org/2000/svg" className="w-10 animate-spin fill-orange-600 block mx-auto" viewBox="0 0 24 24">
                <path d="M12 22c5.421 0 10-4.579 10-10h-2c0 4.337-3.663 8-8 8s-8-3.663-8-8c0-4.336 3.663-8 8-8V2C6.579 2 2 6.58 2 12c0 5.421 4.579 10 10 10z" />
              </svg>
            ) : error ? (
              <p>Error: {error}</p>
            ) : (
              <div className="font-[sans-serif]">
                <div className="flex justify-between items-center mb-4">
                  <h1 className="text-2xl font-bold text-gray-900">News</h1>
                  <button
                    className="bg-[#F2A549] font-normal cursor-pointer text-black px-4 py-2 rounded hover:bg-[#efb46f]"
                    onClick={() => {
                      reset({ title: "", description: "", file: [] });
                      setUploadedFiles([]);
                      setShowModal(true);
                    }}
                  >
                    Add New Article
                  </button>
                </div>

                <div className='w-auto max-w-full overflow-x-auto'>
                  <table className="border-collapse min-w-full border border-gray-300 rounded-lg">
                    <thead className="bg-[#F2A549] text-white">
                      <tr className='text-center'>
                        <th className="p-4 font-normal text-black">Image</th>
                        <th className="p-4 font-normal text-black">Title</th>
                        <th className="p-4 font-normal text-black">Description</th>
                        <th className="p-4 font-normal text-black">Date</th>
                        <th className="p-4 font-normal text-black">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.isArray(News) && [...News].reverse().map((item) => (
                        <tr className="border border-gray-300" key={item.id}>
                          <td className="border border-gray-300 py-2">
                            <div className="flex -space-x-2 overflow-hidden justify-center">
                              <img
                                alt=""
                                src={baseUrl + item.image}
                                className="inline-block size-8 rounded-full ring-2 ring-white outline -outline-offset-1 outline-black/5"
                              />
                            </div>
                          </td>
                          <td className="text-center text-sm border border-gray-300">{item.title}</td>
                          <td className="relative w-[20%] border border-gray-300 text-sm text-center">
                            <div className="w-[90px] truncate inline-block align-middle">
                              {item.description.replace(/<\/?[^>]+(>|$)/g, "")}
                            </div>
                          </td>
                          <td className="text-sm border border-gray-300 text-center">{formatDate(item.created_at)}</td>
                          <td className="border border-gray-300 text-center">
                            <div className='flex justify-center gap-4'>
                              <button className="cursor-pointer" title="Edit" onClick={() => handleEditNew(item)}>
                                <BiEdit size={25} />
                              </button>
                              <button className="cursor-pointer" title="Delete" onClick={() => {
                                setNewToDelete(item);
                                setShowConfirmModal(true);
                              }}>
                                <BiTrash size={25} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {News.length === 0 && (
                        <tr>
                          <td colSpan="5" className="p-4 text-center text-gray-500 border border-gray-300">
                            No News available.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Create Modal */}
                <Dialog open={showModal} onClose={() => setShowModal(false)} className="relative z-[9999]">
                  <DialogBackdrop transition className="fixed inset-0 bg-gray-500/75 transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in" />
                  <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div className="flex min-h-screen items-center justify-center p-4 text-center sm:items-center sm:p-0">
                      <DialogPanel transition className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all data-closed:translate-y-4 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in sm:my-8 sm:w-full max-w-[70%] data-closed:sm:translate-y-0 data-closed:sm:scale-95">
                        <form onSubmit={handleSubmit(onSubmit)}>
                          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                            <div className="sm:col-span-4 mb-3">
                              <label className="block text-sm/6 font-medium text-gray-900">Title</label>
                              <div className="mt-2">
                                <input
                                  id="title"
                                  name="title"
                                  type="text"
                                  autoComplete="title"
                                  {...register('title', { required: 'Title is required' })}
                                  className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 border outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-[#F2A549] sm:text-sm/6"
                                />
                                {errors.title && <span className="error-message">{errors.title.message}</span>}
                              </div>
                            </div>

                            <div className="sm:col-span-4 mb-3">
                              <label className="block text-sm/6 font-medium text-gray-900">Description</label>
                              <div className="mt-2">
                                <Controller
                                  name="description"
                                  control={control}
                                  rules={{ required: "Description is required" }}
                                  render={({ field }) => (
                                    <CKEditor
                                      editor={ClassicEditor}
                                      data={field.value}
                                      onChange={(event, editor) => field.onChange(editor.getData())}
                                    />
                                  )}
                                />
                                {errors.description && <span className="error-message">{errors.description.message}</span>}
                              </div>
                            </div>

                            <div className="sm:col-span-4 mb-3">
                              <label className="block text-sm/6 font-medium text-gray-900">Image</label>
                              <div className="mt-2">
                                <Controller
                                  name="file"
                                  control={control}
                                  rules={{
                                    validate: (value) => {
                                      if (!value || value.length === 0) {
                                        return "Image is required";
                                      }
                                      return true;
                                    }
                                  }}
                                  render={({ field }) => {
                                    const { getRootProps, getInputProps, isDragActive } = useDropzone({
                                      accept: { 'image/jpeg': ['.jpeg', '.jpg'], 'image/png': ['.png'] },
                                      multiple: false,
                                      onDrop: (acceptedFiles) => {
                                        if (acceptedFiles.length > 0) uploadFiles(acceptedFiles, field);
                                      }
                                    });
                                    return (
                                      <>
                                        <div {...getRootProps()} className="dropzone border-2 border-dashed border-gray-300 p-6 mb-4 cursor-pointer hover:border-gray-400 transition-colors">
                                          <input {...getInputProps()} />
                                          <div className="text-center">
                                            {isDragActive ? (
                                              <p className="text-blue-600">Drop the files here ...</p>
                                            ) : (
                                              <div>
                                                <p className="text-gray-600">Drag & drop images here, or click to select</p>
                                                <p className="text-sm text-gray-400 mt-1">Supports: JPG, JPEG, PNG</p>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                        {isUploading && <div className="mb-4"><p className="text-blue-600">Uploading files...</p></div>}
                                        {uploadedFiles.length > 0 && (
                                          <div className="mb-4">
                                            <h4 className="font-medium mb-2">Uploaded Files:</h4>
                                            <ul className="space-y-2">
                                              {uploadedFiles.map((file, i) => (
                                                <li key={i} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                                                  <img width={100} src={baseUrl + file} className='rounded-full' alt="uploaded" />
                                                  <button type="button" onClick={() => removeFile(i, field)} className="text-red-600 hover:text-red-800 text-sm">
                                                    Remove
                                                  </button>
                                                </li>
                                              ))}
                                            </ul>
                                          </div>
                                        )}
                                      </>
                                    );
                                  }}
                                />
                                {errors.file && <span className="error-message">{errors.file.message}</span>}
                              </div>
                            </div>
                          </div>

                          <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                            <button type="submit" className="inline-flex w-full cursor-pointer justify-center rounded-md bg-[#F2A549] px-3 py-2 text-sm font-semibold text-black shadow-xs hover:bg-[#f1c40f] sm:ml-3 sm:w-auto">
                              Create
                            </button>
                            <button type="button" onClick={() => setShowModal(false)} className="mt-3 inline-flex w-full cursor-pointer justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs inset-ring inset-ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto">
                              Cancel
                            </button>
                          </div>
                        </form>
                      </DialogPanel>
                    </div>
                  </div>
                </Dialog>

                {/* Edit Modal */}
                <Dialog open={showEditModal} onClose={() => setShowEditModal(false)} className="relative z-[9999]">
                  <DialogBackdrop transition className="fixed inset-0 bg-gray-500/75 transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in" />
                  <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div className="flex min-h-screen items-center justify-center p-4 text-center sm:items-center sm:p-0">
                      <DialogPanel transition className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all data-closed:translate-y-4 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in sm:my-8 sm:w-full max-w-[70%] data-closed:sm:translate-y-0 data-closed:sm:scale-95">
                        <form onSubmit={handleSubmit(onUpdateSubmit)}>
                          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                            <div className="sm:col-span-4 mb-3">
                              <label className="block text-sm/6 font-medium text-gray-900">Title</label>
                              <div className="mt-2">
                                <input
                                  id="title"
                                  name="title"
                                  type="text"
                                  autoComplete="title"
                                  {...register('title', { required: 'Title is required' })}
                                  className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 border outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-[#F2A549] sm:text-sm/6"
                                />
                                {errors.title && <span className="error-message">{errors.title.message}</span>}
                              </div>
                            </div>

                            <div className="sm:col-span-4 mb-3">
                              <label className="block text-sm/6 font-medium text-gray-900">Description</label>
                              <div className="mt-2">
                                <Controller
                                  name="description"
                                  control={control}
                                  rules={{ required: "Description is required" }}
                                  render={({ field }) => (
                                    <CKEditor
                                      editor={ClassicEditor}
                                      data={formData.description}
                                      onChange={(event, editor) => field.onChange(editor.getData())}
                                    />
                                  )}
                                />
                                {errors.description && <span className="error-message">{errors.description.message}</span>}
                              </div>
                            </div>

                            <div className="sm:col-span-4 mb-3">
                              <label className="block text-sm/6 font-medium text-gray-900">Image</label>
                              <div className="mt-2">
                                <Controller
                                  name="file"
                                  control={control}
                                  rules={{
                                    validate: (value) => {
                                      // ✅ Check both form value and uploadedFiles state
                                      if ((!value || value.length === 0) && uploadedFiles.length === 0) {
                                        return "Image is required";
                                      }
                                      return true;
                                    }
                                  }}
                                  render={({ field }) => {
                                    // ✅ Sync field with uploadedFiles when field is empty but files exist
                                    if ((!field.value || field.value.length === 0) && uploadedFiles.length > 0) {
                                      field.onChange(uploadedFiles);
                                    }
                                    const { getRootProps, getInputProps, isDragActive } = useDropzone({
                                      accept: { 'image/jpeg': ['.jpeg', '.jpg'], 'image/png': ['.png'] },
                                      multiple: false,
                                      onDrop: (acceptedFiles) => {
                                        if (acceptedFiles.length > 0) uploadFiles(acceptedFiles, field);
                                      }
                                    });
                                    return (
                                      <>
                                        <div {...getRootProps()} className="dropzone border-2 border-dashed border-gray-300 p-6 mb-4 cursor-pointer hover:border-gray-400 transition-colors">
                                          <input {...getInputProps()} />
                                          <div className="text-center">
                                            {isDragActive ? (
                                              <p className="text-blue-600">Drop the files here ...</p>
                                            ) : (
                                              <div>
                                                <p className="text-gray-600">Drag & drop images here, or click to select</p>
                                                <p className="text-sm text-gray-400 mt-1">Supports: JPG, JPEG, PNG</p>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                        {isUploading && <div className="mb-4"><p className="text-blue-600">Uploading files...</p></div>}
                                        {uploadedFiles.length > 0 && (
                                          <div className="mb-4">
                                            <h4 className="font-medium mb-2">Uploaded Files:</h4>
                                            <ul className="space-y-2">
                                              {uploadedFiles.map((file, i) => (
                                                <li key={i} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                                                  <img width={100} src={baseUrl + file} className='rounded-full' alt="uploaded" />
                                                  <button type="button" onClick={() => removeFile(i, field)} className="text-red-600 hover:text-red-800 text-sm">
                                                    Remove
                                                  </button>
                                                </li>
                                              ))}
                                            </ul>
                                          </div>
                                        )}
                                      </>
                                    );
                                  }}
                                />
                                {errors.file && <span className="error-message">{errors.file.message}</span>}
                              </div>
                            </div>
                          </div>

                          <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                            <button type="submit" className="inline-flex w-full justify-center cursor-pointer rounded-md bg-[#F2A549] px-3 py-2 text-sm font-semibold text-black shadow-xs hover:bg-[#f1c40f] sm:ml-3 sm:w-auto">
                              Update
                            </button>
                            <button type="button" onClick={() => setShowEditModal(false)} className="mt-3 inline-flex w-full cursor-pointer justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs inset-ring inset-ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto">
                              Cancel
                            </button>
                          </div>
                        </form>
                      </DialogPanel>
                    </div>
                  </div>
                </Dialog>

                <PaginationControls
                  itemsPerPage={itemsPerPage}
                  totalItems={News.length}
                  paginate={paginate}
                  currentPage={currentPage}
                />
              </div>
            )}
          </div>
        </div>

        <ConfirmationModal
          show={showConfirmModal}
          title={newToDelete ? newToDelete.title : ''}
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
        />
      </div>
    </DashboardTemplate>
  );
}

export default News;